import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useToast } from 'src/Contexts/ToastContext';
import { useUpdateInstallationMutation } from 'src/queries';
import type { Installation } from 'src/types';

import './FeatureVisibilityControl.css';

interface FeatureVisibilityControlProps {
  installation: Installation;
}

const MIN_RADIUS = 1.5;
const MAX_RADIUS = 8;
const DEFAULT_RADIUS = 3.5;

/**
 * Admin-only control on the Status page: per-installation visualization settings.
 * - timeline / deviation: what NON-admin users see (admins always see both).
 * - MPLE overlay, interpolation: HARD on/off for everyone incl. admins.
 * - point size: electrode circle radius for everyone.
 * All persist to installation.configuration.
 */
const FeatureVisibilityControl = ({ installation }: FeatureVisibilityControlProps) => {
  const { t } = useTranslation();
  const toast = useToast();
  const updateInstallation = useUpdateInstallationMutation();

  const cfg = installation.configuration ?? {};
  // Defaults: timeline on (undefined => true), deviation off (undefined => false),
  // MPLE on (undefined => true), interpolation on (undefined => true), radius 3.5.
  const timelineOn = cfg.timeline_visible !== false;
  const deviationOn = cfg.deviation_visible === true;
  const mpleOn = cfg.mple_visible !== false;
  const interpolationOn = cfg.interpolation_visible !== false;
  const radius = typeof cfg.point_radius === 'number' ? cfg.point_radius : DEFAULT_RADIUS;

  // Local slider state so dragging is smooth; the mutation fires on release.
  const [radiusDraft, setRadiusDraft] = useState<number>(radius);
  // Keep the draft in sync when the installation (and thus its saved radius)
  // changes — the component is remounted per installation via `key`, but guard
  // anyway per React's adjust-state-on-prop-change pattern.
  const [radiusFor, setRadiusFor] = useState(installation.id);
  if (radiusFor !== installation.id) {
    setRadiusFor(installation.id);
    setRadiusDraft(radius);
  }

  const save = (patch: Partial<Installation['configuration']>) => {
    updateInstallation.mutate(
      {
        id: installation.id,
        data: { configuration: { ...cfg, ...patch } } as Partial<Installation>,
      },
      { onSuccess: () => toast.success(t('featureVis.saved')) }
    );
  };

  return (
    <div className="feature-vis" role="group" aria-label={t('featureVis.title')}>
      <span className="feature-vis-title">{t('featureVis.title')}</span>
      <label className="feature-vis-item">
        <input
          type="checkbox"
          checked={timelineOn}
          disabled={updateInstallation.isPending}
          onChange={(e) => save({ timeline_visible: e.target.checked })}
        />
        {t('featureVis.timeline')}
      </label>
      <label className="feature-vis-item">
        <input
          type="checkbox"
          checked={deviationOn}
          disabled={updateInstallation.isPending}
          onChange={(e) => save({ deviation_visible: e.target.checked })}
        />
        {t('featureVis.deviation')}
      </label>
      <label className="feature-vis-item">
        <input
          type="checkbox"
          checked={mpleOn}
          disabled={updateInstallation.isPending}
          onChange={(e) => save({ mple_visible: e.target.checked })}
        />
        {t('featureVis.mple')}
      </label>
      <label className="feature-vis-item">
        <input
          type="checkbox"
          checked={interpolationOn}
          disabled={updateInstallation.isPending}
          onChange={(e) => save({ interpolation_visible: e.target.checked })}
        />
        {t('featureVis.interpolation')}
      </label>
      <label className="feature-vis-item feature-vis-item--slider">
        <span>
          {t('featureVis.pointSize')}: {radiusDraft.toFixed(1)}
        </span>
        <input
          type="range"
          min={MIN_RADIUS}
          max={MAX_RADIUS}
          step={0.5}
          value={radiusDraft}
          disabled={updateInstallation.isPending}
          onChange={(e) => setRadiusDraft(Number(e.target.value))}
          // Persist on release so we don't fire a mutation per drag tick.
          onMouseUp={() => save({ point_radius: radiusDraft })}
          onTouchEnd={() => save({ point_radius: radiusDraft })}
          onKeyUp={() => save({ point_radius: radiusDraft })}
        />
      </label>
    </div>
  );
};

export default FeatureVisibilityControl;
